import { usePostMatchWiresIncomingMutation } from 'API/accounting/AccountingApi';
import { IPotentialMatch, useGetPotentialEscrowFile, useGetPotentialOrderInfo } from './IPotentialMatch';
import React, { useEffect, useState } from 'react';
import { IEscrowExpectedIncomingWireData } from 'types/accounting/data/IEscrowExpectedIncomingWireData';
import { checkForMutationSuccess } from 'API/checkForMutationSuccess';
import { ErrorMessageTW } from 'tailwindComponents/errorMessage/ErrorMessageTW';
import { convertToError } from 'API/BaseApi';
import {
  useCreateWireDepositMutation,
  useDeleteACHDepositMutation,
  useDeleteCheckDepositMutation,
} from 'API/order/escrow/EscrowBaseAPI';
import { ConfirmWireForm } from './ConfirmWireForm';
import { IWireForm } from './Forms';
import { SpinnerTW } from 'tailwindComponents/spinnerTW/SpinnerTW';
import { PotentialDisplay } from './PotentialDisplay';
import { assertDefined } from 'utilities/Assertions';

export interface IConfirmWireMatchProps {
  actualId: string;
  accountNumber: string;
  potentialMatch: IPotentialMatch;
  onClose: () => void;
  amountCents: number;
  amountCentsMax: number;
}

export const ConfirmWireMatch: React.FC<IConfirmWireMatchProps> = ({
  actualId,
  accountNumber,
  potentialMatch,
  onClose,
  amountCents,
  amountCentsMax,
}) => {
  const order = useGetPotentialOrderInfo(potentialMatch);
  const escrowFile = useGetPotentialEscrowFile(potentialMatch);
  const [doMatch, { error }] = usePostMatchWiresIncomingMutation();
  const [createIncomingWire, { isLoading: isLoadingCreateIncomingWire }] = useCreateWireDepositMutation();
  const [deleteIncomingCheck] = useDeleteCheckDepositMutation();
  const [deleteIncomingACH] = useDeleteACHDepositMutation();
  const [expectedWire, setExpectedWire] = useState<IEscrowExpectedIncomingWireData | undefined>(
    potentialMatch.expectedWire
  );
  useEffect(() => {
    setExpectedWire(potentialMatch.expectedWire);
  }, [potentialMatch]);

  const onMatch = async () => {
    assertDefined(expectedWire);

    const result = await doMatch({ actualId, expectedId: expectedWire.id });

    if (checkForMutationSuccess(result, 'matching wire')) {
      if (potentialMatch.expectedCheck?.id) {
        await deleteIncomingCheck({ id: potentialMatch.expectedCheck.id, orderId: order.id });
      }
      if (potentialMatch.expectedACH?.id) {
        await deleteIncomingACH({ id: potentialMatch.expectedACH?.id, orderId: order.id });
      }
      onClose();
    }
  };

  const onCreate = async (x: IWireForm) => {
    if (order?.id && escrowFile?.id) {
      const result = await createIncomingWire({ ...x, orderId: order.id, escrowFileId: escrowFile.id });

      checkForMutationSuccess(result, 'creating an expected wire');
      if ('data' in result) {
        setExpectedWire({
          ...result.data,
          wireIdentifier: result.data.wireIdentifier || '',
          escrowFile: { ...escrowFile, order: order } as any,
        });
      }
    }
  };

  return (
    <>
      <ErrorMessageTW error={convertToError(error)} />
      {!expectedWire && !isLoadingCreateIncomingWire && (
        <>
          <ConfirmWireForm
            potentialMatch={potentialMatch}
            createExpected={onCreate}
            amountCents={amountCents}
            amountCentsMax={amountCentsMax}
          />
        </>
      )}
      {isLoadingCreateIncomingWire && <SpinnerTW />}
      <PotentialDisplay
        potentialMatch={expectedWire ? { expectedWire } : potentialMatch}
        accountNumber={accountNumber}
        onMatch={expectedWire && onMatch}
      />
    </>
  );
};
